import { useState } from 'react';
import { Box, Chip, Divider, Popover, Typography } from '@mui/material';
import { formatDuration, timeTicks } from '../../../utils/stackTimeline';
import { CK, FONT_BODY, emptyTextSx, statusChipSx } from '../portalStyles';

// Segment / cap colours, following the AWS CloudFormation timeline: blue in
// progress, green complete, red failed, light grey rollback (here also the
// StackSets cleanup) in progress, dark grey rollback complete, and red/white
// hatching for the likely root cause.
const ROLLBACK_BG = '#E3E7EC';
const ROLLBACK_EDGE = '#9AA5B1';
const ROLLBACK_DONE = '#5F6B7A';

const hatch = (color, bg = CK.surface) => (
  `repeating-linear-gradient(135deg, ${color} 0 3px, ${bg} 3px 6px)`
);

const SEGMENT_STYLE = {
  progress: { backgroundColor: CK.blue },
  rollback: { backgroundColor: ROLLBACK_BG, border: `1px solid ${ROLLBACK_EDGE}` },
};
const INFERRED_STYLE = {
  progress: { background: hatch(`${CK.blue}99`), border: `1px dashed ${CK.blue}` },
  rollback: { background: hatch(ROLLBACK_EDGE, ROLLBACK_BG), border: `1px dashed ${ROLLBACK_EDGE}` },
};
const CAP_STYLE = {
  complete: { backgroundColor: CK.green },
  failed: { backgroundColor: CK.red },
  rollback_complete: { backgroundColor: ROLLBACK_DONE },
};
const ROOT_CAUSE_STYLE = { background: hatch(CK.red), border: `1px solid ${CK.red}` };

// Swatch for one status in the details popover; `rollback` is true when the
// resource's activity is a rollback or a StackSets cleanup.
const statusSwatch = (status, rollback) => {
  if (/_FAILED$/.test(status)) return CAP_STYLE.failed;
  const rb = rollback || /ROLLBACK/.test(status);
  if (/_COMPLETE$|_SKIPPED$/.test(status)) return rb ? CAP_STYLE.rollback_complete : CAP_STYLE.complete;
  return rb ? SEGMENT_STYLE.rollback : SEGMENT_STYLE.progress;
};

const LABEL_WIDTH = 230;
const ROW_HEIGHT = 30;
const BAR_HEIGHT = 16;
const CAP_WIDTH = 8;
const AXIS_HEIGHT = 34;

const captionSx = { fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted };
const detailLabelSx = { fontFamily: FONT_BODY, fontSize: '0.7rem', fontWeight: 600, color: CK.navy, mt: 1 };
const detailValueSx = { fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body };

const clockTime = (t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

function Axis({ ticks, pct, bottom }) {
  return (
    <Box sx={{ display: 'flex', height: AXIS_HEIGHT }}>
      <Box sx={{ width: LABEL_WIDTH, flexShrink: 0 }} />
      <Box sx={{ flex: 1, position: 'relative' }}>
        {ticks.map((t) => (
          <Typography
            key={t}
            sx={{
              ...captionSx,
              position: 'absolute',
              left: `${pct(t)}%`,
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              ...(bottom ? { top: 4 } : { bottom: 4 }),
            }}
          >
            {clockTime(t)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

// ResourceDetails is the popover AWS shows when a resource is chosen: type,
// start / end / total duration, every status the resource went through with
// its time and how long it lasted, and the failure reason.
function ResourceDetails({ bar }) {
  const evs = bar.events;
  const rbSeg = bar.segments.find((g) => g.kind === 'rollback');
  const rbCap = bar.caps.find((c) => c.kind === 'rollback_complete');
  const cleanupFrom = rbSeg ? rbSeg.start : (rbCap ? rbCap.at : null);
  return (
    <Box sx={{ p: 2, width: 360, maxWidth: '90vw' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, color: CK.navy, wordBreak: 'break-all' }}>
          {bar.logicalId}
        </Typography>
        {bar.rootCause && <Chip size="small" label="Likely root cause" sx={statusChipSx('error')} />}
      </Box>
      <Typography sx={detailLabelSx}>Type</Typography>
      <Typography sx={detailValueSx}>{bar.type || '—'}</Typography>
      {bar.physicalId && (
        <>
          <Typography sx={detailLabelSx}>Physical ID</Typography>
          <Typography sx={{ ...detailValueSx, wordBreak: 'break-all' }}>{bar.physicalId}</Typography>
        </>
      )}
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box>
          <Typography sx={detailLabelSx}>Start time</Typography>
          <Typography sx={detailValueSx}>{clockTime(bar.from)}</Typography>
        </Box>
        <Box>
          <Typography sx={detailLabelSx}>End time</Typography>
          <Typography sx={detailValueSx}>{clockTime(bar.to)}</Typography>
        </Box>
        <Box>
          <Typography sx={detailLabelSx}>Total duration</Typography>
          <Typography sx={detailValueSx}>{formatDuration(bar.to - bar.from)}</Typography>
        </Box>
      </Box>
      {bar.segments.some((g) => g.inferred) && (
        <Typography sx={{ ...captionSx, mt: 1 }}>
          AWS logged no start event for part of this resource&apos;s activity; the hatched span is
          assumed to start when the operation started.
        </Typography>
      )}
      <Divider sx={{ my: 1.25 }} />
      <Typography sx={{ ...detailLabelSx, mt: 0 }}>Statuses</Typography>
      {evs.slice().reverse().map((e, i) => {
        const at = new Date(e.timestamp).getTime();
        const next = evs[evs.length - i];
        const lasted = next ? new Date(next.timestamp).getTime() - at : null;
        // In a merged create + cleanup, events after the first DELETE are the cleanup.
        const rb = cleanupFrom !== null && at >= cleanupFrom;
        return (
          <Box key={e.eventId || `${e.timestamp}-${e.resourceStatus}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.75 }}>
            <Box sx={{ width: 10, height: 10, mt: '3px', borderRadius: '2px', flexShrink: 0, ...statusSwatch(e.resourceStatus, rb) }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...detailValueSx, fontWeight: 600, color: CK.navy }}>
                {e.resourceStatus}
              </Typography>
              <Typography sx={captionSx}>
                {clockTime(at)}{lasted !== null && lasted > 0 ? ` · ${formatDuration(lasted)}` : ''}
              </Typography>
            </Box>
          </Box>
        );
      })}
      {bar.reason && (
        <>
          <Typography sx={detailLabelSx}>{bar.caps.some((c) => c.kind === 'failed') ? 'Failure reason' : 'Reason'}</Typography>
          <Typography sx={{ ...detailValueSx, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{bar.reason}</Typography>
        </>
      )}
    </Box>
  );
}

// StackTimeline draws one attempt (from buildAttempts, or withCleanup for a
// failed create with its cleanup) as a Gantt chart in the style of the AWS
// CloudFormation timeline: one row per logical ID ordered by start, each bar
// split into status segments with a cap for each terminal status, a time grid
// above and below, and a details popover when a resource is chosen. Plain MUI
// Box elements, no chart library.
export default function StackTimeline({ attempt }) {
  const [selected, setSelected] = useState(null); // { bar, anchor }
  if (!attempt || attempt.bars.length === 0) {
    return (
      <Typography sx={{ ...emptyTextSx, fontSize: '0.875rem' }}>
        No resource activity in this attempt.
      </Typography>
    );
  }
  const start = attempt.startedAt;
  const span = Math.max(attempt.endedAt - start, 1000);
  const pct = (t) => Math.min(100, Math.max(0, ((t - start) / span) * 100));
  const ticks = timeTicks(start, start + span);
  const merged = attempt.cleanupStatus !== undefined;
  const hasRollback = attempt.bars.some((b) => b.segments.some((g) => g.kind === 'rollback')
    || b.caps.some((c) => c.kind === 'rollback_complete'));
  const rollbackWord = merged || attempt.cleanup ? 'Cleanup' : 'Rollback';
  const legend = [
    ['In progress', SEGMENT_STYLE.progress],
    ['Complete', CAP_STYLE.complete],
    ['Failed', CAP_STYLE.failed],
    ...(hasRollback ? [
      [`${rollbackWord} in progress`, SEGMENT_STYLE.rollback],
      [`${rollbackWord} complete`, CAP_STYLE.rollback_complete],
    ] : []),
    ...(attempt.bars.some((b) => b.rootCause) ? [['Likely root cause', ROOT_CAUSE_STYLE]] : []),
    ...(attempt.bars.some((b) => b.segments.some((g) => g.inferred))
      ? [['No start event (assumed)', INFERRED_STYLE.progress]] : []),
  ];

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 640, px: 3 }}>
        <Typography sx={{ ...captionSx, fontWeight: 600, color: CK.body, mb: 0.5 }}>
          Total {formatDuration(attempt.endedAt - attempt.startedAt)}
          {merged && ' · failed create and its automatic cleanup'}
        </Typography>
        <Axis ticks={ticks} pct={pct} />

        <Box sx={{ position: 'relative' }}>
          {/* Grid: one vertical line per tick, across every row. */}
          <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: LABEL_WIDTH, right: 0, pointerEvents: 'none' }}>
            {ticks.map((t) => (
              <Box key={t} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(t)}%`, borderLeft: `1px solid ${CK.border}` }} />
            ))}
          </Box>

          {attempt.bars.map((bar) => (
            <Box key={bar.logicalId} sx={{ display: 'flex', alignItems: 'center', height: ROW_HEIGHT }}>
              <Box
                sx={{
                  width: LABEL_WIDTH, flexShrink: 0, pr: 1.5, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', textAlign: 'right', fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.navy,
                }}
                title={bar.logicalId}
              >
                {bar.logicalId}
              </Box>
              <Box
                role="button"
                tabIndex={0}
                aria-label={`${bar.logicalId} details`}
                onClick={(e) => setSelected({ bar, anchor: e.currentTarget })}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected({ bar, anchor: e.currentTarget }); } }}
                sx={{
                  flex: 1, position: 'relative', height: '100%', cursor: 'pointer',
                  borderTop: `1px solid ${CK.border}`, borderBottom: `1px solid ${CK.border}`, mb: '-1px',
                  '&:hover': { backgroundColor: `${CK.hover}99` },
                  '&:focus-visible': { outline: `2px solid ${CK.blue}`, outlineOffset: -2 },
                }}
              >
                {bar.segments.map((g) => (
                  <Box
                    key={`s-${g.kind}-${g.start}`}
                    sx={{
                      position: 'absolute', top: (ROW_HEIGHT - BAR_HEIGHT) / 2 - 1, height: BAR_HEIGHT, borderRadius: '2px',
                      left: `${pct(g.start)}%`, width: `${Math.max(pct(g.end) - pct(g.start), 0)}%`, minWidth: 3,
                      boxSizing: 'border-box',
                      ...(g.inferred ? INFERRED_STYLE[g.kind] : SEGMENT_STYLE[g.kind]),
                    }}
                  />
                ))}
                {bar.caps.map((c) => (
                  <Box
                    key={`c-${c.kind}-${c.at}`}
                    sx={{
                      position: 'absolute', top: (ROW_HEIGHT - BAR_HEIGHT) / 2 - 1, height: BAR_HEIGHT, width: CAP_WIDTH,
                      left: `max(0px, calc(${pct(c.at)}% - ${CAP_WIDTH}px))`, borderRadius: '2px', boxSizing: 'border-box',
                      ...(c.kind === 'failed' && bar.rootCause ? ROOT_CAUSE_STYLE : CAP_STYLE[c.kind]),
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Axis ticks={ticks} pct={pct} bottom />

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
          {legend.map(([label, style]) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '2px', boxSizing: 'border-box', ...style }} />
              <Typography sx={captionSx}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Popover
        open={!!selected}
        anchorEl={selected?.anchor || null}
        onClose={() => setSelected(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {selected && <ResourceDetails bar={selected.bar} />}
      </Popover>
    </Box>
  );
}
